/**
 * The guide of the configuration pages, as a law firm reads it: what the
 * calculation takes each input to mean. Each text states what the engine does
 * today. A page's entries stand in the order of their markers on the page, as
 * listed in `components/guide/guidePages.ts`.
 *
 * An insurer's texts are in `guideInsurer.ts`.
 */
export const guideTerms = {
  button: "How these inputs are read",
  title: "How these inputs are read",
  close: "Close the guide",
  intro:
    "What the calculation takes each input on this page to mean. Select a marker on the page to open its entry here.",
  youEnter: "You enter",
  assumes: "The calculation assumes",
  example: "Example",
  throughout: "Throughout this page",
  explain: "Explain “{{title}}”",
  caseWide: {
    title: "Assumptions that hold for the whole case",
    entries: {
      outcomes: {
        title: "Every combination of issues",
        text: "The calculation goes through every combination of issues won and lost. Each combination is an outcome with its own chance, and the figures are those outcomes and their average, weighted by the chances.",
      },
      trial: {
        title: "Decided at Trial, all or nothing",
        text: "What no accepted settlement ends is decided at Trial. Partial findings and appeals are not modelled.",
      },
      dates: {
        title: "No dates in the figures",
        text: "No figure depends on a date: there is no interest on damages or costs, no discounting and no cost inflation.",
      },
      estimates: {
        title: "An estimate is one value",
        text: "Where you give several weighted estimates, of an asset or of an award, their weighted average is the value used, the same in every outcome.",
      },
      payment: {
        title: "Paid as far as assets reach",
        text: "A court order is paid as far as the paying party's assets reach. Where several parties owe the same sum, or one party owes several others, each party's net results assume the order of payment that is worst for it, and its settlement and bargaining ranges the order that is best for it against each opponent. So the figures of different parties need not add up.",
      },
      settlements: {
        title: "Settlements",
        text: "Only an accepted offer changes what is decided. The settlement costs entered with an offer are spent at its stage whatever becomes of it.",
      },
    },
  },
  pages: {
    parties: {
      entries: {
        client: {
          title: "Clients and non-clients",
          enter: "In the party editor, whether the party is your client.",
          assumes:
            "Every party's outcomes are calculated the same way, client or not. A client comes first in the analytics, the Agreements page is for clients, and your firm's revenue and profit come from its clients alone.",
        },
        assets: {
          title: "Assets",
          enter:
            "Unlimited, or limited with a list of assets, each with one or more value estimates and their weights.",
          assumes:
            "A court order against a party, for damages or costs, is paid as far as its assets reach. Each asset counts at the weighted average of its estimates, the same in every outcome, and the party's limit is the sum of its assets. Limited with no asset listed means it can pay no court order. A party's own costs and settlement sums are paid in full and do not use up the limit.",
          example:
            "An asset valued at £0 or at £1,000,000 with equal weights counts as £500,000 in every outcome: of an award of £800,000, £500,000 is collected.",
        },
      },
      general: {
        effect: {
          title: "What it changes in the figures",
          text: "A party's limited assets lower what can be collected from it, and so the net results of those it owes.",
        },
        removal: {
          title: "Removing a party",
          text: "Removing a party deletes the claims it brought and takes it off the claims against it. Its share of the other parties' apportionment goes too, with the parts of their costs towards it, so their budgets fall by those amounts.",
        },
      },
    },
    settlement: {
      entries: {
        chances: {
          title: "Chance of settling",
          enter:
            "For each stage to come: how likely the two settle at it, if they are still in dispute when it comes.",
          assumes:
            "A stage counts only if no stage before it settled, and opponents settle independently of each other. Nothing entered is 0%, which leaves every figure as litigated. A settlement happens at the start of its stage, so that stage's estimates are not spent, and it costs nothing to negotiate. Stages after the judgment take no chance yet.",
          example:
            "40% at Disclosure and 50% at Witness statements: 40% settle at Disclosure, half of the remaining 60% at Witness statements, which is 30%, and 30% go on.",
        },
        position: {
          title: "Where in the bargaining range",
          enter:
            "Where the two settle: 0% is the worst for this party, 100% the worst for its opponent, 50% the midpoint.",
          assumes:
            "It applies to each stage to come, in the range of that stage. Create offer drafts an offer at the amount shown, for all that is left between the two. A stage without a range has nothing to agree on: it takes no chance.",
        },
        ifFirst: {
          title: "If another opponent settles first",
          enter:
            "Where the two would settle, as a share of their bargaining range.",
          assumes:
            "Another opponent is liable for a claimed amount this offer covers. If it settles first, less is left to claim, so the amount of this offer no longer applies. The two settle at this share of the range that is left then. That range is only known once the other settlement is in, so there is no amount to show.",
          example:
            "£1,000,000 is claimed from two opponents, and one settles first with £600,000 going to it. £400,000 is left to claim from the other, and 50% is the midpoint of the range on that, whatever this offer says.",
        },
      },
      general: {
        adjusted: {
          title: "What it changes in the figures",
          text: "The litigation path stays as it is. Every combination of settlements is calculated in full, with its bills, fees, VAT and recoveries, and weighted by its chance. The result is shown beside it as settlement-adjusted: a switch on the analytics pages and the portfolio chooses between them. Reserves stay on the litigation path.",
        },
        offerAhead: {
          title: "With an offer on the table",
          text: "The chances of later stages assume a settlement based on that offer: the current stage and every later one settle at its amount. Where the offer leaves the range of a later stage, the stage is marked, as one side would do better by litigating; the chance still counts, so set it to 0% if you do not expect that.",
        },
        sequences: {
          title: "Several opponents at one stage",
          text: "Nothing says who settles first, so each sequence is calculated, every one as likely as the next.",
        },
        shared: {
          title: "A claimed amount owed by several opponents",
          text: "A settlement pays all else before the claimed amount, so that as much as possible stays to be recovered from the others. What it pays towards the claimed amount leaves less to claim from them.",
        },
      },
    },
    timeline: {
      entries: {
        dates: {
          title: "Start of the case",
          enter: "The day the case started.",
          assumes:
            "The timeline counts from it: the first stage is expected to end its duration after this day, and each stage after it counts from the end of the one before.",
        },
        duration: {
          title: "Duration",
          enter: "How many months you expect a stage to take.",
          assumes:
            "Only what you enter is planned: nothing is filled in for you. A stage without a duration has no expected end, and nor has any stage that counts from it.",
        },
        ended: {
          title: "Closing a stage",
          enter:
            "The day the stage ended and, for each client and cost type, what was actually spent.",
          assumes:
            "Closing moves the case on to the next stage. Spending less than planned leaves the rest an estimate; spending more raises the budget by the difference, apportioned as the costs so far. Other parties' costs at the stage stay as they are. Reopening the stage closed last takes its end date away and keeps its costs.",
        },
        status: {
          title: "Status",
          enter: "Nothing here: it follows from the dates.",
          assumes:
            "Closed: the stage has ended. Open: the case is in this stage. Overdue: it is open past its expected end, and the stages after it keep the dates you planned until you enter a new duration or close it. Not planned: it has no expected end. Settled: every claim was settled in this stage, on the day the offer that completed it was accepted, and the stages after it are not reached; the case opens again once that offer is no longer accepted.",
        },
      },
      general: {
        effect: {
          title: "What it changes in the figures",
          text: "Dates and durations change no figure: they plan the case. Closing a stage can, through the costs it records.",
        },
        standing: {
          title: "Where the case stands",
          text: "The open stage is where the case stands. The case list shows it, and the Cost progress slider on the Costs page moves within it.",
        },
      },
    },
    claims: {
      entries: {
        parties: {
          title: "Claimant and defendants",
          enter: "Who brings the claim, and against whom.",
          assumes:
            "The claim is decided against each defendant: a defendant-specific issue separately for each of them, a generic issue once for all. The claimant is paid each claimed amount once, however many defendants lose. Costs follow the claim against each defendant: whoever wins it recovers costs from the other side, at its rates on the Recoveries page.",
        },
        settled: {
          title: "Settled with a defendant",
          enter:
            "Nothing here: settlements are agreed on the Settlements page.",
          assumes:
            "Once an offer is accepted, the claims it settles leave the calculation for that defendant. The part of the settlement sum apportioned to a claimed amount is deducted from it for the defendants that remain.",
        },
        chances: {
          title: "Win chances",
          enter:
            "For each issue, the chance that the claimant wins it against each defendant.",
          assumes:
            "A win chance is the chance of establishing the issue at Trial, all or nothing. Issues are treated as independent of each other: where one depends on another, enter its chance assuming the other is won. A missing chance stops the calculation; 0% means the issue is lost for certain.",
          example:
            "Against one defendant, issue 1 at 70% and issue 2 at 80%: the claim is won against it with 70% × 80% = 56%.",
        },
        kind: {
          title: "Defendant-specific or generic",
          enter:
            "Whether the issue has a win chance per defendant, or one for all of them.",
          assumes:
            "Defendant-specific: a chance of its own against each defendant, each decided separately. Generic: one chance and one decision shared by all the defendants, won against all of them or against none.",
          example:
            "A generic issue at 60% against two defendants is won against both with 60%, and against neither with 40%.",
        },
        relation: {
          title: "How issues combine",
          enter: "Between two issues: And, And / Or, or Either or.",
          assumes:
            "And: both must be won. And / Or: at least one of the grouped issues must be won, each independently of the others. Either or: exactly one can be won; each chance is that alternative alone, and the rest is the chance that none is won. Together they cannot exceed 100%: grouping scales the chances per defendant down to fit, and a generic chance that takes a group above 100% leaves it with no outcomes. Issues that are not grouped combine with And.",
        },
        summary: {
          title: "Against all, against any",
          enter: "Nothing here: the calculation works them out.",
          assumes:
            "Under each defendant, the chance of winning the claim against it. Against all: against every defendant still in the case. Against any: against at least one of them. A settled defendant no longer counts.",
        },
        amounts: {
          title: "Claimed amounts and award estimates",
          enter:
            "Each damage with the amount claimed, and one or more estimates of the share a court would award if the claim succeeds.",
          assumes:
            "An amount is awarded only if the whole claim is won against a defendant liable for it: winning single issues gives nothing. Several award estimates are averaged by their weights into one award, used in every outcome where the claim is won. A damage claimed in several claims is awarded once, at the highest amount among the claims won. No interest is added.",
          example:
            "Award estimates of 90% and 80% with equal weights give one award of 85%: £799,000 of the £940,000 claimed, in every outcome where the claim is won.",
        },
        liability: {
          title: "Liability of each defendant",
          enter:
            "For each claimed amount, the share of the award each defendant can be made to pay.",
          assumes:
            "Each share is the most that defendant pays. 100% for each means any of them can be made to pay all of it; shares adding up to 100% mean each pays its own; if they add up to less, the rest is never collected. The claimant is paid once in all, as far as the defendants' assets reach. Contribution between defendants is not modelled.",
          example:
            "Of an award of £799,000, a defendant at 100% can be made to pay all of it and another at 40% up to £319,600, but together they pay no more than £799,000.",
        },
      },
      general: {
        effect: {
          title: "What it changes in the figures",
          text: "The chances weight every outcome of the case and the amounts decide what each outcome is worth: together they drive the net results and the expected value.",
        },
        type: {
          title: "Claim type",
          text: "Groups and filters cases across the portfolio. It changes no figure of the case.",
        },
        counterclaims: {
          title: "Counterclaims",
          text: "Enter a counterclaim as a claim of its own with the roles reversed. It is decided independently of the claim it answers, and its costs follow it separately.",
        },
      },
    },
    costs: {
      entries: {
        editor: {
          title: "Costs per stage and type",
          enter:
            "For each stage, the party's law firm fees and its disbursements: court fees, barrister fees, expert fees and others, without VAT.",
          assumes:
            "An outcome spends the costs of the stages it reaches: up to Trial, and for a party that settles everything only up to its last settlement. A party spends its Enforcement costs only in an outcome where it collects from an opponent: the part towards that opponent and the generic part. Costs of a stage an outcome does not reach are not spent, incurred or not, though a CFA's base counts them (see the Agreements page). Appeals have no costs of their own here.",
        },
        vat: {
          title: "VAT",
          enter:
            "Whether the party can reclaim the VAT on its costs and, if it cannot, its rate.",
          assumes:
            "A party that reclaims VAT has none in its figures and recovers none. A party that cannot pays it at its rate on all it pays for work, CFA fees included, court fees excepted, and recovers it with its costs, at the rate of each cost type.",
        },
        progress: {
          title: "Cost progress",
          enter:
            "With the slider under the chart, how much of the open stage's costs has been spent.",
          assumes:
            "Incurred costs have been spent; estimated costs are still to be spent. The slider moves the costs of the open stage alone: the stages before it keep what closing them recorded, and those after it stay estimated. To go on to the next stage, close this one.",
        },
        breakdown: {
          title: "Cost breakdown",
          enter: "Nothing here: it sums up the costs above.",
          assumes:
            "The party's costs by how far they are spent and by cost type, without VAT. Where the party cannot reclaim VAT, the total above the chart adds it.",
        },
      },
      general: {
        effect: {
          title: "What it changes in the figures",
          text: "A party's costs lower its net result in every outcome that spends them, and what it recovers of them raises it again. Under a CFA they also set the fees.",
        },
        settlements: {
          title: "After a settlement",
          text: "Once a settlement ends the case with an opponent, what was incurred towards it at acceptance stays spent, and what was still estimated towards it is no longer to be spent. The Cost editor shows what settlements fixed beside its inputs, and the slider works on the rest. A party that has settled with all its opponents is out of the case: all its costs are fixed.",
        },
        settlementCosts: {
          title: "Settlement costs",
          text: "The costs of negotiating a settlement are entered with each offer, by cost type, and come on top of this budget.",
        },
      },
    },
    costStructure: {
      entries: {
        indirect: {
          title: "Indirect costs",
          enter:
            "The share of your firm's fee that goes to its indirect costs.",
          assumes:
            "One share for all your firm's work on the case, at every stage. Indirect and direct costs together are what the work costs your firm.",
        },
        direct: {
          title: "Direct costs",
          enter: "The share of your firm's fee that goes to its direct costs.",
          assumes:
            "One share for all your firm's work on the case, at every stage. Indirect and direct costs together are what the work costs your firm.",
        },
        profit: {
          title: "Profit",
          enter: "Nothing here: what remains of the fee after both.",
          assumes:
            "Your firm's profit on a fee it is paid in full. Where a CFA leaves its work unpaid, the firm still bears what the work costs it, so that work shows as a loss.",
        },
      },
      general: {
        clients: {
          title: "Your firm and other firms",
          text: "The structure is that of your firm, which acts for the clients. No other party's law firm gets a profit: its fees are costs of that party alone.",
        },
        missing: {
          title: "Without a structure",
          text: "Nothing is assumed: your firm's revenue still counts, and its profit stays unknown until a structure is entered.",
        },
      },
    },
    agreements: {
      entries: {
        lawFirmCfa: {
          title: "Law firm CFA",
          enter:
            "Whether the party's law firm works under a conditional fee agreement.",
          assumes:
            "The fees the agreement covers fall due only once it is known whether a success step is reached. If one is, the client pays the base costs and a success fee. If none is, it pays the no-win fee, if there is one, and the firm bears the rest. Reaching a step is not the same as winning: it depends on what the step measures.",
        },
        covered: {
          title: "Covered base costs",
          enter:
            "Nothing to type: they come from the party's budget. You say whether barrister fees are covered.",
          assumes:
            "While the case goes on, the base is the party's whole budget of the types covered, less what settlements left not to be spent, plus its settlement costs of those types: work at a stage an outcome never reaches counts too. Once the party has settled everything, the base is what it has incurred. A CFA covers law firm fees, and barrister fees where switched on; no other disbursements.",
        },
        lost: {
          title: "If the case is lost",
          enter:
            "The no-win fee, as a share of the covered base costs; 0% means no win, no fee.",
          assumes:
            "Where no success step is reached, the no-win fee is all that is due. It is guaranteed, so the success fee is worked out on the rest of the base, the part at risk.",
        },
        success: {
          title: "Success conditions",
          enter: "One or more steps, each with a success fee percentage.",
          assumes:
            "The success fee is the highest percentage among the steps reached, of the base at risk, on top of the base costs.",
          example:
            "Base costs of £50,000 with a 30% no-win fee leave £35,000 at risk. A 50% success fee adds £17,500, so £67,500 is due. If no step is reached, £15,000 is due.",
        },
        trigger: {
          title: "Triggered by",
          enter:
            "What the steps measure: Amount awarded, Amount recovered or Amount to pay.",
          assumes:
            "Amount awarded: damages and costs awarded, plus settlements received. Amount recovered: settlements received, plus what the party collects on a judgment, less what it pays and its enforcement costs. Amount to pay: settlements paid, and the same balance after a judgment, which suits a defendant. Where several stages give a value, the best running total counts. A party with no value at all is assessed at its last settlement.",
        },
        barrister: {
          title: "Barrister CFA",
          enter:
            "Whether the barrister works under a CFA, and who engages the barrister.",
          assumes:
            "Engaged by the party: beside the law firm's CFA, the party pays the barrister its base fees and success fee or, where no step of the barrister's CFA is reached, its no-win fee, if it has one. Engaged by the law firm, whose CFA then covers barrister fees: the firm pays the barrister, and those payments, success fee included, form part of the firm's base, with the firm's own success fee on top.",
        },
      },
      general: {
        recovery: {
          title: "What is recovered",
          text: "A success fee is never recovered from an opponent. Base costs the party pays are recovered at its rates on the Recoveries page; work it does not pay for is not.",
        },
        vat: {
          title: "VAT",
          text: "A party that cannot reclaim VAT pays it on its CFA fees too. The VAT on a success fee is never recovered.",
        },
        mocks: {
          title: "Third-party funding and ATE",
          text: "Shown for what is to come: they cannot be switched on, and they change no figure.",
        },
      },
    },
    recoveries: {
      entries: {
        tabs: {
          title: "One set of rates per party",
          enter: "The rates of the party whose tab is open.",
          assumes:
            "A party's rates apply to its own costs when it is the one recovering them. The rates of the opponent that pays play no part.",
        },
        awarded: {
          title: "Likely to be awarded",
          enter:
            "For each kind of cost, the share of this party's costs a court would likely order an opponent to pay when the party succeeds against it.",
          assumes:
            "The party succeeds against an opponent when it wins a claim against it, or when that opponent's claim against the party fails. One rate covers all its costs of that kind, at every stage, spent and still to spend. A rate left at 0% recovers nothing.",
          example:
            "A party wins its claim against its only opponent. Of £88,500 of law firm fees, at 70% the opponent is ordered to pay £61,950, as far as its assets allow. The party bears the other £26,550 itself.",
        },
        total: {
          title: "Total costs",
          enter: "Nothing here: the totals come from the Costs page.",
          assumes:
            "All of the party's costs of that kind as budgeted, at every stage, with VAT where the party cannot reclaim it. It still counts what settlements left no longer to be spent. The settlement costs entered with offers are not in this total: they are recovered at the same rates, from the offer's counter-party alone.",
        },
        recovery: {
          title: "Resulting recovery",
          enter: "Nothing here: it is the rate times the total costs.",
          assumes:
            "The most the party can recover before any settlement. Each opponent it succeeds against owes its own share from the Apportionment page plus all costs apportioned to no single opponent; with no apportionment, each owes all of it. The party is paid once, as far as the opponents' assets reach.",
        },
      },
      general: {
        bothWays: {
          title: "Wins and losses against one opponent",
          text: "If the party wins one claim against an opponent and loses another, each side recovers its own costs from the other, at its own rates. No court weighs the two results against each other.",
        },
        paid: {
          title: "Only what was paid",
          text: "A party recovers only what it pays. Under a CFA, where no success step is reached, it recovers none of the base costs, and a success fee is never recovered.",
        },
        vat: {
          title: "VAT",
          text: "A party that cannot reclaim VAT recovers it with its costs, at the same rates. A party that reclaims VAT recovers none.",
        },
        settlements: {
          title: "After a settlement",
          text: "What an accepted settlement covers of the party's costs is not recovered again, at trial or by a later settlement. Costs no longer to be spent are not recovered either.",
        },
      },
    },
    apportionments: {
      entries: {
        shares: {
          title: "Towards each opponent",
          enter:
            "The share of the party's costs incurred against each opponent.",
          assumes:
            "Each share is of the party's full budget. An opponent is liable for the costs towards it only when the party wins against it. Moving a share moves incurred and estimated costs at their ratio, so the cost progress stays as it is.",
        },
        generic: {
          title: "Generic costs",
          enter: "Nothing here: what no opponent is apportioned.",
          assumes:
            "Costs the party bears that cannot be allocated to any one opponent. Every opponent it wins against is liable for all of them, and the party recovers no more than 100% of its costs.",
          example:
            "£100,000 of costs recovered at 100%, shares of 50% and 30%, 20% generic. The party beats both opponents: the first is charged £70,000, the second £50,000, and it recovers £100,000. It beats only the first: that one is charged £70,000.",
        },
        modes: {
          title: "Whole budget or per stage",
          enter:
            "One slider per opponent for the whole budget, or sliders and amounts stage by stage.",
          assumes:
            "A top slider moves the opponent's share at every stage alike. Per stage, each stage takes its own split; while the stages differ, the top sliders show what they come to and cannot be moved.",
        },
        liability: {
          title: "Liability for costs",
          enter: "Nothing here: it follows from the shares.",
          assumes:
            "What each opponent is liable for when the party wins against it: its own share and all of the generic costs.",
        },
        bargaining: {
          title: "In the bargaining ranges",
          enter: "Nothing here: it follows from the shares.",
          assumes:
            "How the party's costs count in its bargaining range with each opponent: the opponent's own share and an equal part of the generic costs. The bargaining ranges alone use this split, and they share out the party's enforcement costs and CFA fees equally too.",
        },
      },
      general: {
        settled: {
          title: "Opponents that settled",
          text: "Settling changes no share: all stay shares of the full budget. Once a settlement ends the case with an opponent, the costs towards it are locked, and the other shares can grow only into generic costs no settlement covers.",
        },
      },
    },
  },
};
